import { useRef, useState } from "react";
import { getLevel } from "@/lib/audio";

const THRESHOLD = 0.035;
const SILENCE_MS = 1300;
const MIN_SPEECH_MS = 450;
const IDLE_MS = 45000;

export default function useHandsFree(onUtterance, onIdle) {
  const [active, setActive] = useState(false);
  const [hearing, setHearing] = useState(false);
  const r = useRef({});
  const cb = useRef(onUtterance);
  cb.current = onUtterance;
  const idleCb = useRef(onIdle);
  idleCb.current = onIdle;

  const loop = () => {
    const s = r.current;
    if (!s.stream) return;
    const now = performance.now();
    const level = getLevel(s.analyser);
    if (!s.paused) {
      if (level > THRESHOLD) {
        s.lastVoice = now;
        s.lastActivity = now;
        if (!s.rec) {
          s.chunks = [];
          s.rec = new MediaRecorder(s.stream);
          s.rec.ondataavailable = (e) => e.data.size && s.chunks.push(e.data);
          s.rec.start();
          s.startedAt = now;
          setHearing(true);
        }
      } else if (s.rec && now - s.lastVoice > SILENCE_MS) {
        const rec = s.rec;
        const long = s.lastVoice - s.startedAt > MIN_SPEECH_MS;
        s.rec = null;
        setHearing(false);
        rec.onstop = () => long && cb.current(new Blob(s.chunks, { type: "audio/webm" }));
        rec.stop();
      } else if (!s.rec && now - s.lastActivity > IDLE_MS) {
        s.lastActivity = now;
        idleCb.current?.();
      }
    }
    s.raf = requestAnimationFrame(loop);
  };

  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    await ctx.resume();
    ctx.createMediaStreamSource(stream).connect(analyser);
    r.current = { stream, ctx, analyser, paused: false, rec: null, lastActivity: performance.now() };
    setActive(true);
    loop();
  };

  const discard = () => {
    const s = r.current;
    if (s.rec) {
      s.rec.onstop = null;
      s.rec.stop();
      s.rec = null;
      setHearing(false);
    }
  };

  const pause = () => { discard(); r.current.paused = true; };
  const resume = () => { r.current.paused = false; r.current.lastActivity = performance.now(); };

  const stop = () => {
    const s = r.current;
    discard();
    cancelAnimationFrame(s.raf);
    s.stream?.getTracks().forEach((t) => t.stop());
    s.ctx?.close();
    r.current = {};
    setActive(false);
  };

  return { active, hearing, start, stop, pause, resume };
}
